/**
 * Product recommendation logic (from Content Product recommendation flow.xlsx).
 * Maps wizard answers to silo content package keys (see web/models.py AVAILABLE_SILO_CONTENT_REPORTS_PACKAGES).
 */

export const TOP_LEVEL_KEYS = {
  OUTLINE: 'TOP_LEVEL_NEW_CONTENT_OUTLINE',
  DRAFT: 'TOP_LEVEL_NEW_CONTENT_AI',
  HUMAN_DRAFT: 'TOP_LEVEL_NEW_CONTENT_AI_HUMAN_EDITED',
  UPGRADE: 'TOP_LEVEL_CONTENT_UPGRADE',
  HUMAN_UPDATE: 'TOP_LEVEL_HUMAN_EDITED_CONTENT_UPDATE',
};

export const SUPPORTING_KEYS = {
  OUTLINE: 'SUPPORTING_NEW_CONTENT_OUTLINE',
  DRAFT: 'SUPPORTING_NEW_CONTENT_AI',
  HUMAN_DRAFT: 'SUPPORTING_NEW_CONTENT_AI_HUMAN_EDITED',
};

// Outline packages are currently disabled in checkout, so the wizard should
// skip the branching questions that only exist to choose those options.
const OUTLINE_OPTIONS_ENABLED = {
  topLevel: false,
  supporting: false,
};

export const QUESTIONS = {
  topPageType: {
    id: 'topPageType',
    text: 'Is this a new page or an existing page?',
    options: [
      { value: 'new', label: 'New page', sublabel: 'Content needs to be created from scratch' },
      { value: 'existing', label: 'Existing page', sublabel: 'Page already exists on your website' },
    ],
  },
  topExistingTreatment: {
    id: 'topExistingTreatment',
    text: 'How would you like us to improve this existing page?',
    options: [
      { value: 'instructions', label: 'SEO instructions only', sublabel: 'A clear list of steps to improve the page' },
      { value: 'write', label: 'Write upgraded content', sublabel: 'We expand and optimize around your existing copy' },
      { value: 'redo', label: 'Redo entire page', sublabel: 'Treat it like new content from scratch' },
    ],
  },
  topNewFullOrOutline: {
    id: 'topNewFullOrOutline',
    text: 'Do you want a fully written page or just an outline with instructions?',
    options: [
      { value: 'full', label: 'Fully written page', sublabel: 'We write the complete article for you' },
      { value: 'outline', label: 'Just an outline', sublabel: 'Outline with SEO instructions for your writers' },
    ],
  },
  topHumanReview: {
    id: 'topHumanReview',
    text: 'Do you want a human writer to review your content before delivery?',
    options: [
      { value: true, label: 'Yes, human review', sublabel: 'Higher quality, reviewed by our editorial team' },
      { value: false, label: 'No, AI is fine', sublabel: 'Faster delivery, great for most pages' },
    ],
  },
  supFullOrOutline: {
    id: 'supFullOrOutline',
    text: 'Do you want a fully written page or just an outline?',
    options: [
      { value: 'full', label: 'Fully written', sublabel: 'We write the complete article' },
      { value: 'outline', label: 'Just an outline', sublabel: 'Outline for your writers' },
    ],
  },
  supHumanReview: {
    id: 'supHumanReview',
    text: 'Do you want a human writer to review your content before delivery?',
    options: [
      { value: true, label: 'Yes, human review', sublabel: 'Higher quality, reviewed by our editorial team' },
      { value: false, label: 'No, AI is fine', sublabel: 'Faster delivery, great for most pages' },
    ],
  },
};

/**
 * @param {object} s
 * @param {'new'|'existing'|null} s.pageType
 * @param {'instructions'|'write'|'redo'|null} s.existingTreatment
 * @param {'full'|'outline'|null} s.newFullOrOutline
 * @param {boolean|null} s.humanReview
 * @returns {string|null} package key
 */
export function computeTopLevelPackageKey(s) {
  if (!s.pageType) return null;
  if (s.pageType === 'existing') {
    if (!s.existingTreatment) return null;
    if (s.existingTreatment === 'instructions') return TOP_LEVEL_KEYS.UPGRADE;
    if (s.existingTreatment === 'write') return TOP_LEVEL_KEYS.HUMAN_UPDATE;
    if (s.existingTreatment === 'redo') {
      if (OUTLINE_OPTIONS_ENABLED.topLevel) {
        if (!s.newFullOrOutline) return null;
        if (s.newFullOrOutline === 'outline') return TOP_LEVEL_KEYS.OUTLINE;
      }
      if (s.humanReview === null || s.humanReview === undefined) return null;
      return s.humanReview ? TOP_LEVEL_KEYS.HUMAN_DRAFT : TOP_LEVEL_KEYS.DRAFT;
    }
    return null;
  }
  if (s.pageType === 'new') {
    if (OUTLINE_OPTIONS_ENABLED.topLevel) {
      if (!s.newFullOrOutline) return null;
      if (s.newFullOrOutline === 'outline') return TOP_LEVEL_KEYS.OUTLINE;
    }
    if (s.humanReview === null || s.humanReview === undefined) return null;
    return s.humanReview ? TOP_LEVEL_KEYS.HUMAN_DRAFT : TOP_LEVEL_KEYS.DRAFT;
  }
  return null;
}

/**
 * @param {object} s
 * @param {'full'|'outline'|null} s.fullOrOutline
 * @param {boolean|null} s.humanReview
 * @returns {string|null}
 */
export function computeSupportingPackageKey(s) {
  if (OUTLINE_OPTIONS_ENABLED.supporting) {
    if (!s.fullOrOutline) return null;
    if (s.fullOrOutline === 'outline') return SUPPORTING_KEYS.OUTLINE;
  }
  if (s.humanReview === null || s.humanReview === undefined) return null;
  return s.humanReview ? SUPPORTING_KEYS.HUMAN_DRAFT : SUPPORTING_KEYS.DRAFT;
}

/** Which question id is currently active for top-level wizard */
export function getActiveTopQuestionId(state) {
  if (!state.pageType) return QUESTIONS.topPageType.id;
  if (state.pageType === 'existing') {
    if (!state.existingTreatment) return QUESTIONS.topExistingTreatment.id;
    if (state.existingTreatment === 'redo') {
      if (OUTLINE_OPTIONS_ENABLED.topLevel) {
        if (!state.newFullOrOutline) return QUESTIONS.topNewFullOrOutline.id;
      }
      if (!OUTLINE_OPTIONS_ENABLED.topLevel || state.newFullOrOutline === 'full') {
        if (state.humanReview === null || state.humanReview === undefined) return QUESTIONS.topHumanReview.id;
      }
      return null;
    }
    return null;
  }
  if (state.pageType === 'new') {
    if (OUTLINE_OPTIONS_ENABLED.topLevel) {
      if (!state.newFullOrOutline) return QUESTIONS.topNewFullOrOutline.id;
    }
    if (!OUTLINE_OPTIONS_ENABLED.topLevel || state.newFullOrOutline === 'full') {
      if (state.humanReview === null || state.humanReview === undefined) return QUESTIONS.topHumanReview.id;
    }
    return null;
  }
  return null;
}

export function getActiveSupportingQuestionId(state) {
  if (OUTLINE_OPTIONS_ENABLED.supporting) {
    if (!state.fullOrOutline) return QUESTIONS.supFullOrOutline.id;
  }
  if (!OUTLINE_OPTIONS_ENABLED.supporting || state.fullOrOutline === 'full') {
    if (state.humanReview === null || state.humanReview === undefined) return QUESTIONS.supHumanReview.id;
  }
  return null;
}

function labelForTopPageType(v) {
  const o = QUESTIONS.topPageType.options.find((x) => x.value === v);
  return o ? o.label : '';
}

function labelForExistingTreatment(v) {
  const o = QUESTIONS.topExistingTreatment.options.find((x) => x.value === v);
  return o ? o.label : '';
}

function labelForFullOrOutline(v) {
  const o = QUESTIONS.topNewFullOrOutline.options.find((x) => x.value === v);
  return o ? o.label : '';
}

function labelForHumanReview(v) {
  const o = QUESTIONS.topHumanReview.options.find((x) => x.value === v);
  return o ? o.label : '';
}

/** @returns {{ questionText: string, answerLabel: string }[]} */
export function getCompletedTopRows(state) {
  const rows = [];
  if (state.pageType) {
    rows.push({
      questionText: QUESTIONS.topPageType.text,
      answerLabel: labelForTopPageType(state.pageType),
    });
  }
  if (state.pageType === 'existing' && state.existingTreatment) {
    rows.push({
      questionText: QUESTIONS.topExistingTreatment.text,
      answerLabel: labelForExistingTreatment(state.existingTreatment),
    });
  }
  const inNewPath = state.pageType === 'new' || state.existingTreatment === 'redo';
  if (OUTLINE_OPTIONS_ENABLED.topLevel && inNewPath && state.newFullOrOutline) {
    rows.push({
      questionText: QUESTIONS.topNewFullOrOutline.text,
      answerLabel: labelForFullOrOutline(state.newFullOrOutline),
    });
  }
  if (
    inNewPath &&
    (!OUTLINE_OPTIONS_ENABLED.topLevel || state.newFullOrOutline === 'full') &&
    (state.humanReview === true || state.humanReview === false)
  ) {
    rows.push({
      questionText: QUESTIONS.topHumanReview.text,
      answerLabel: labelForHumanReview(state.humanReview),
    });
  }
  return rows;
}

function labelSupFullOrOutline(v) {
  const o = QUESTIONS.supFullOrOutline.options.find((x) => x.value === v);
  return o ? o.label : '';
}

function labelSupHuman(v) {
  const o = QUESTIONS.supHumanReview.options.find((x) => x.value === v);
  return o ? o.label : '';
}

/** @returns {{ questionText: string, answerLabel: string }[]} */
export function getCompletedSupportingRows(state) {
  const rows = [];
  if (OUTLINE_OPTIONS_ENABLED.supporting && state.fullOrOutline) {
    rows.push({
      questionText: QUESTIONS.supFullOrOutline.text,
      answerLabel: labelSupFullOrOutline(state.fullOrOutline),
    });
  }
  if (
    (!OUTLINE_OPTIONS_ENABLED.supporting || state.fullOrOutline === 'full') &&
    (state.humanReview === true || state.humanReview === false)
  ) {
    rows.push({
      questionText: QUESTIONS.supHumanReview.text,
      answerLabel: labelSupHuman(state.humanReview),
    });
  }
  return rows;
}

export function createEmptyTopWizardState() {
  return {
    pageType: null,
    existingTreatment: null,
    newFullOrOutline: null,
    humanReview: null,
  };
}

export function createEmptySupportingWizardState() {
  return {
    fullOrOutline: null,
    humanReview: null,
  };
}

/**
 * Reset answers from a completed-row index (0 = first answered question).
 * Must stay aligned with getCompletedTopRows ordering.
 */
export function resetTopWizardFromStep(state, rowIndex) {
  if (rowIndex <= 0) return createEmptyTopWizardState();
  const next = { ...state };
  if (state.pageType === 'existing') {
    if (rowIndex === 1) {
      next.existingTreatment = null;
      next.newFullOrOutline = null;
      next.humanReview = null;
      return next;
    }
    if (rowIndex === 2 && state.existingTreatment === 'redo') {
      next.newFullOrOutline = null;
      next.humanReview = null;
      return next;
    }
    if (rowIndex === 3 && state.existingTreatment === 'redo') {
      next.humanReview = null;
      return next;
    }
    return next;
  }
  if (state.pageType === 'new') {
    if (rowIndex === 1) {
      next.newFullOrOutline = null;
      next.humanReview = null;
      return next;
    }
    if (rowIndex === 2) {
      next.humanReview = null;
      return next;
    }
  }
  return next;
}

export function resetSupportingWizardFromStep(state, stepIndex) {
  const next = { ...state };
  if (stepIndex <= 0) {
    next.fullOrOutline = null;
    next.humanReview = null;
    return next;
  }
  next.humanReview = null;
  return next;
}
